/**
 * VideoFullscreenButton
 *
 * Generic, theme-aware "enter fullscreen" affordance for any video surface.
 * Extracted from `ContentComponentOverlays` so the exact same control is shared
 * across the content feed, the encrypted-media `InlineVideoPlayer` (chat /
 * Community group-room video), and any future video surface — keeping fullscreen
 * playback visually consistent throughout the app.
 *
 * Pure presentation: it renders the icon button and forwards the click. The
 * caller decides what fullscreen means (typically
 * `videoManagement.enterVideoFullscreen(videoKey)` → native browser fullscreen,
 * which exposes the native controls while fullscreen).
 */

import React from 'react';

import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface VideoFullscreenButtonProps {
  /** Invoked when the button is tapped — caller triggers fullscreen. */
  onClick: () => void;

  /** Accessible label; defaults to a generic "Enter fullscreen". */
  ariaLabel?: string;
}

export const VideoFullscreenButton: React.FC<VideoFullscreenButtonProps> = ({
  onClick,
  ariaLabel = 'Enter fullscreen',
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <button
      onClick={onClick}
      className="rounded-full transition-colors"
      style={{ backgroundColor: theme.transparent, pointerEvents: 'auto', padding: responsive.spacing.xs }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${theme.white}1A`)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.transparent)}
      aria-label={ariaLabel}
    >
      <AssetIcon
        path="/assets/images/full-screen.png"
        width={responsive.icon.small}
        height={responsive.icon.small}
      />
    </button>
  );
};
